import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import farmsmojoVideo from '../../../images/farmsmojo.mp4';
import styles from './Farmsmojo.module.css';

const FarmsmojoProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Idea Generation and Conceptualization', icon: '💡' },
    { text: 'User Experience (UX) and Interface (UI) Design', icon: '🎯' },
    { text: 'Content Management', icon: '📝' },
    { text: 'E-commerce Integration', icon: '🛒' },
    { text: 'Tech Stack Management', icon: '⚙️' },
    { text: 'Performance Optimization', icon: '🚀' },
    { text: 'Collaboration with Backend Team', icon: '🤝' },
    { text: 'Security Measures', icon: '🔒' }
  ];

  const tools = [
    { name: 'WordPress', icon: '🔧', description: 'CMS Platform' },
    { name: 'Figma', icon: '🎨', description: 'Design Tool' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Illustrator', icon: '✨', description: 'Vector Graphics' },
    { name: 'CorelDraw', icon: '🎭', description: 'Graphic Design' }
  ];

  const skills = [
    'CSS', 'Figma', 'HTML5', 'Svelte', 'Flutter', 
    'CorelDraw', 'WordPress', 'JavaScript', 'Tailwind CSS', 'Adobe Animate'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Organic Products eCommerce Platform</h1>
                <p className={styles.projectSubtitle}>Cultivating Ideas For Growth</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Designing</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2022</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>July 2021</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  System ensures transparent, quality products by sourcing efficiently. We're our first customers, spreading the mojo's essence. This is a WooCommerce website connecting customers with wholesome, sustainable products.
                </p>
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={farmsmojoVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                This platform was developed to promote conscious living by making wholesome, sustainably sourced products easily accessible through a reliable digital marketplace. It emphasizes transparency, ethical sourcing, and environmental responsibility while maintaining a seamless shopping experience. By bridging the gap between producers and consumers, the platform enables users to explore carefully curated organic products sourced from different regions across India. The ecosystem is designed to foster trust, encourage sustainable consumption, and support responsible agricultural practices.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                As a Web Developer, I was responsible for building and maintaining the platform’s online infrastructure using WooCommerce. My contributions included implementing responsive user interfaces, optimizing performance, and ensuring smooth end-to-end eCommerce functionality—from product discovery to secure checkout. I worked closely with stakeholders to translate sustainability-driven business goals into a scalable, user-centric digital experience while maintaining high standards of usability, reliability, and transparency.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default FarmsmojoProjectShowcase;